import * as THREE from "three";

/**
 * WIND — one shared vertex-shader sway for every soft thing in the world
 * (banners, flags, awnings, tree canopies, bushes, kelp).
 *
 * Why a shader and not a JS animation: the things that should move in the wind
 * are exactly the things this renderer has spent sixteen passes MERGING —
 * canopies are one InstancedMesh for a whole map, banners are one merged mesh.
 * Animating them on the CPU would mean un-merging them (hundreds of draw calls)
 * or rewriting a vertex buffer every frame (a per-frame upload of tens of
 * thousands of vertices). A vertex program costs ONE uniform write per frame
 * for the entire world, and the geometry stays GPU-resident and merged.
 *
 * Contract for a wind material:
 *  - its geometry MUST carry a `swayW` float attribute in [0,1] — 0 is pinned
 *    (a trunk root, a banner's top hem), 1 is free (a crown tip, a flag's fly
 *    edge). A geometry without it gets the GL default 0 and simply does not
 *    move, which is a silent no-op, so `windGeometry()` below exists to make
 *    setting it a one-liner and the offline bench asserts every wind geometry
 *    has one.
 *  - `uWindT`/`uWindDir` are SHARED uniform objects (the same object is handed
 *    to every material), so `tickWind()` writes one number and the whole world
 *    moves. Amplitude and frequency are per-material uniforms, NOT baked into
 *    the source, so every wind material compiles to the SAME program — one
 *    shader variant for the entire feature (`bench-warmcov` counts variants).
 *
 * Known, accepted limitation: NORMALS are not re-derived from the sway. At
 * these amplitudes (a few cm to ~0.35 m) the lighting error is far below the
 * texture's own normal detail; re-deriving would need the tangent basis
 * differentiated per vertex, which is a lot of ALU for a shading difference
 * nobody can see on a banner. Shadow maps have the same caveat — every wind
 * mesh in this game is `castShadow = false` (instanced decor always was), so
 * the depth pass never disagrees with the colour pass.
 */

/** Shared across every wind material — written once per frame by tickWind(). */
export const windTime = { value: 0 };
/** Horizontal wind direction (unit XZ). Constant for now; a per-map breeze
 *  would be one assignment here. */
export const windDir = { value: new THREE.Vector2(0.82, 0.57).normalize() };

/** One uniform write per frame for the whole world. Called from render(). */
export function tickWind(nowMs: number): void {
  windTime.value = nowMs * 0.001;
}

/** The sway itself. Phase comes from WORLD position (per-instance translation
 *  plus the vertex's own offset), so neighbouring props are never in lockstep
 *  and a gust visibly travels across a tree line. Three sines at incommensurate
 *  rates: a slow breathing swell, the main sway, and a fast flutter. */
const CHUNK = /* glsl */`
  float phase = uWindT * uWindFreq
    + (wOrigin.x + position.x) * 0.21 + (wOrigin.z + position.z) * 0.17;
  float gust = 0.34 * sin(phase * 0.31)
    + 0.62 * sin(phase)
    + 0.26 * sin(phase * 2.37 + 1.7);
  // the vertical term is signed by |gust|, not gust: a sheet SHORTENS as it
  // swings either way (it is pivoting, not stretching), and a canopy that rose
  // on half its cycle read as breathing rather than as wind.
  vec3 sway = vec3(uWindDir.x * gust, -abs(gust) * 0.25, uWindDir.y * gust)
    * (uWindAmp * swayW);
  #ifdef USE_INSTANCING
    // sway is a WORLD-space offset, but transformed is object space and the
    // instance matrix carries a random yaw — applying it directly would make
    // every tree lean its own way instead of the wind's. Undo the instance's
    // rotation+uniform scale: for R*s, inverse = transpose/s^2, and
    // (v * M) in GLSL is transpose(M) * v.
    mat3 im3 = mat3(instanceMatrix);
    transformed += (sway * im3) / max(1e-6, dot(im3[0], im3[0]));
  #else
    transformed += sway;
  #endif
`;

/**
 * Give a material the sway. Returns the same material (chainable).
 *
 * @param amp   metres of horizontal travel at `swayW == 1`, at full gust.
 * @param freq  radians/second of the main sine — heavier cloth, slower.
 */
export function applyWind<T extends THREE.Material>(mat: T, amp: number, freq = 1.0): T {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWindT = windTime;     // SHARED object — one write moves all
    shader.uniforms.uWindDir = windDir;    // SHARED object
    shader.uniforms.uWindAmp = { value: amp };
    shader.uniforms.uWindFreq = { value: freq };
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>
        attribute float swayW;
        uniform float uWindT;
        uniform float uWindAmp;
        uniform float uWindFreq;
        uniform vec2 uWindDir;`)
      .replace("#include <begin_vertex>", `#include <begin_vertex>
        {
          vec3 wOrigin = vec3(0.0);
          #ifdef USE_INSTANCING
            wOrigin = instanceMatrix[3].xyz;
          #endif
          ${CHUNK}
        }`);
  };
  // Without this, three keys the program on `onBeforeCompile.toString()` — every
  // wind material has the SAME closure source, so a non-wind material with the
  // same defines could collide, and (worse) three would not know these differ
  // from stock. One constant key: all wind materials share one program.
  mat.customProgramCacheKey = () => "npWind1";
  return mat;
}

/**
 * Stamp a `swayW` attribute onto a geometry.
 *
 * @param geo geometry to annotate (mutated, returned).
 * @param w   per-vertex weight from the vertex's LOCAL position.
 */
export function windGeometry(
  geo: THREE.BufferGeometry,
  w: (x: number, y: number, z: number) => number,
): THREE.BufferGeometry {
  const pos = geo.getAttribute("position");
  const arr = new Float32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    arr[i] = Math.max(0, Math.min(1, w(pos.getX(i), pos.getY(i), pos.getZ(i))));
  }
  geo.setAttribute("swayW", new THREE.BufferAttribute(arr, 1));
  return geo;
}

/** The weight curve every UPRIGHT plant uses: nothing at the root, quadratic
 *  into the canopy. Shared so a trunk and the crown that sits on it agree at
 *  the join — they are separate meshes with separate materials, and if their
 *  curves disagreed the crown would visibly slide off the bole. */
export function uprightSway(y: number, base: number, top: number): number {
  const t = (y - base) / Math.max(1e-6, top - base);
  return t <= 0 ? 0 : t >= 1 ? 1 : t * t;
}
